import type { MouseEvent, ReactNode } from 'react';
import { Loader } from '@mantine/core';
import metaClasses from './CardMetaBadge.module.css';
import legacyClasses from './ProductCardOverlayChip.module.css';

export type ProductCardOverlayChipVariant = 'price' | 'views' | 'star' | 'starred';

const OVERLAY_VARIANT_CLASS: Record<ProductCardOverlayChipVariant, string> = {
  price: metaClasses.overlayPrice,
  views: metaClasses.overlayViews,
  star: metaClasses.overlayStar,
  starred: metaClasses.overlayStarred,
};

type OverlayChipProps = {
  variant: ProductCardOverlayChipVariant;
  icon?: ReactNode;
  children: ReactNode;
  title?: string;
  className?: string;
  interactive?: boolean;
  loading?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

type LegacyChipProps = {
  variant?: 'default' | 'stat';
  icon: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md';
  interactive?: boolean;
  starred?: boolean;
  loading?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
};

export type ProductCardOverlayChipProps = OverlayChipProps | LegacyChipProps;

function isOverlayChip(props: ProductCardOverlayChipProps): props is OverlayChipProps {
  return (
    props.variant === 'price' ||
    props.variant === 'views' ||
    props.variant === 'star' ||
    props.variant === 'starred'
  );
}

export function ProductCardOverlayChip(props: ProductCardOverlayChipProps) {
  if (isOverlayChip(props)) {
    const {
      variant,
      icon,
      children,
      title,
      className,
      interactive = false,
      loading = false,
      onClick,
      disabled = false,
      ariaLabel,
    } = props;

    const isIconOnly =
      children === '' || children === null || children === undefined || children === false;

    const chipClassName = [
      metaClasses.badge,
      metaClasses.overlay,
      OVERLAY_VARIANT_CLASS[variant],
      interactive ? metaClasses.overlayInteractive : '',
      isIconOnly ? metaClasses.overlayIconOnly : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    const iconNode = loading ? (
      <Loader size={10} color="gray" />
    ) : icon ? (
      <span className={metaClasses.icon}>{icon}</span>
    ) : null;

    const content = (
      <>
        {iconNode}
        {children !== '' && children != null ? (
          <span className={`${metaClasses.label} ${metaClasses.overlayValue}`}>{children}</span>
        ) : null}
      </>
    );

    if (interactive) {
      return (
        <button
          type="button"
          className={chipClassName}
          title={title}
          onClick={onClick}
          disabled={disabled || loading}
          aria-label={ariaLabel}
        >
          {content}
        </button>
      );
    }

    return (
      <span className={chipClassName} title={title}>
        {content}
      </span>
    );
  }

  const {
    icon,
    children,
    size = 'sm',
    variant = 'default',
    interactive = false,
    starred = false,
    loading = false,
    onClick,
    disabled = false,
    ariaLabel,
    className,
  } = props;

  const sizeClass = size === 'md' ? legacyClasses.sizeMd : legacyClasses.sizeSm;
  const interactiveClass =
    interactive && variant === 'stat'
      ? legacyClasses.chipStatButton
      : interactive
        ? legacyClasses.chipButton
        : '';
  const chipClassName = [
    legacyClasses.chip,
    sizeClass,
    interactiveClass,
    starred ? legacyClasses.chipStarred : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconNode = loading ? (
    <Loader size={size === 'md' ? 14 : 10} color="gray" />
  ) : (
    <span className={legacyClasses.chipIcon}>{icon}</span>
  );

  if (interactive) {
    return (
      <button
        type="button"
        className={chipClassName}
        onClick={onClick}
        disabled={disabled || loading}
        aria-label={ariaLabel}
      >
        {iconNode}
        <span>{children}</span>
      </button>
    );
  }

  return (
    <span className={chipClassName}>
      {iconNode}
      <span>{children}</span>
    </span>
  );
}
